// engine-exception: auth/onboarding flow, not a resource screen — stays hand-written (blueprint §1: web/src/pages is legacy)
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getSupabase } from '../lib/supabase';
import { AuthCard, AuthScene, CheckIcon } from '../components/authui';

// Set a new password. Reached two ways:
//   1. After entering a recovery CODE on the login screen (verifyOtp type=recovery) — a
//      session is already established when we land here.
//   2. By clicking a recovery LINK in the email (if it survives mail scanners) — supabase-js
//      exchanges it on load and fires PASSWORD_RECOVERY, establishing the session.
// Either way we need a live session to call updateUser; without one we point back to login.

export function ResetPasswordPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [ready, setReady] = useState(Boolean(session));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Catch the recovery-link case: supabase fires PASSWORD_RECOVERY once the URL is exchanged.
  useEffect(() => {
    if (session) { setReady(true); return; }
    const { data: sub } = getSupabase().auth.onAuthStateChange((event, s) => {
      if (event === 'PASSWORD_RECOVERY' || s) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, [session]);

  async function submit() {
    setError(null);
    if (password.length < 8) return setError('Use at least 8 characters.');
    if (password !== confirm) return setError('Those passwords don’t match.');
    setBusy(true);
    try {
      const { error } = await getSupabase().auth.updateUser({ password });
      if (error) throw error;
      setDone(true);
      setTimeout(() => navigate('/', { replace: true }), 1100);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update your password.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthScene>
        <AuthCard>
          <div style={{ textAlign: 'center', padding: 'var(--sp-3) 0' }}>
            <div className="success-check"><CheckIcon /></div>
            <h1 style={{ fontSize: 'var(--fs-20)' }}>Password updated</h1>
            <p className="muted" style={{ marginTop: 6 }}>Taking you to your workspace…</p>
          </div>
        </AuthCard>
      </AuthScene>
    );
  }

  if (!ready) {
    return (
      <AuthScene>
        <AuthCard title="Reset link needed" subtitle="This page sets a new password after you verify a reset code or link.">
          <p className="muted" style={{ textAlign: 'center', lineHeight: 1.5 }}>
            Start from the sign-in screen and tap <strong>Forgot password</strong> to get a reset code.
          </p>
          <Link className="btn primary block lift" style={{ marginTop: 'var(--sp-3)' }} to="/login">
            Back to sign in
          </Link>
        </AuthCard>
      </AuthScene>
    );
  }

  return (
    <AuthScene>
      <AuthCard>
        <div className="auth-head">
          <h1>Set a new password</h1>
          <p>Choose a new password for your account.</p>
        </div>
        <form className="auth-form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <div className="field">
            <label>New password</label>
            <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={8} autoFocus />
          </div>
          <div className="field">
            <label>Confirm password</label>
            <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" required minLength={8} />
          </div>
          <button type="submit" className="btn primary block lift" disabled={busy}>
            {busy ? 'Updating…' : 'Update password'}
          </button>
          {error && <div className="auth-msg err">⚠ {error}</div>}
        </form>
      </AuthCard>
    </AuthScene>
  );
}
